import { Injectable, QueryList } from '@angular/core';
import { Router, Params, NavigationStart, NavigationEnd, NavigationExtras } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { UtilService } from '../util/util.service';
import { INavigationService } from './inavigation.service';
import { Observable } from 'rxjs';
import { NameValue } from '../model/name-value.model';
import { UserService } from '../../users/shared/user.service';
import { ApiService } from '../api/api.service';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '../dialog/dialog.service';
import { LocalStorageService } from '../storage/local-storage.service';
import { AuthService } from '../auth/auth.service';
import { ConfigService } from 'src/app/config/shared/config.service';
import { BetaFeatureToggleEnum } from 'src/app/config/shared/constants/beta-feature-toggle.enum';
import { FormV2TypeEnum } from 'src/app/form-builder/shared/enum/form-v2-type.enum';
import { formatDate } from '@angular/common';
import { AppManagerServiceApiService } from '../api/app-manager-api.service';
import { AuthUserInfo } from '../auth/auth-user-info.model';
import { AppManagerService } from '../appmanager/appmanager.service';

@Injectable()
export class NavigationService {
  public _utilService: UtilService;
  public _router: Router;
  public _userService: UserService;
  // public _apiService: ApiService;
  public _translateService: TranslateService;
  public _dialogService: DialogService;
  public _localStorageService: LocalStorageService;
  public _authService: AuthService;

  public routeParams: Array<NameValue>;

  public legacyDomain = '';

  public constructor(
    utilService: UtilService,
    router: Router,
    userService: UserService,
    translateService: TranslateService,
    dialogService: DialogService,
    localStorageService: LocalStorageService,
    authService: AuthService,
    private _configService: ConfigService,
    private _appManagerService: AppManagerService
  ) {
    this._utilService = utilService;
    this._router = router;
    this._userService = userService;
    this._translateService = translateService;
    this._dialogService = dialogService;
    this._localStorageService = localStorageService;
    this._authService = authService;

    this.routeParams = new Array<NameValue>();

    this._authService.getUserInfo().then((data: any) => {
      this.legacyDomain = data.wli_tenant_https_url || '';
    });
  }

  public getLegacyDomain = (): string => {
    return this.legacyDomain;
  };

  public navigate = (url: string, queryParams?: Params): Promise<boolean> => {
    if (queryParams) {
      return this._router.navigate([url], { queryParams: queryParams });
    } else {
      return this._router.navigate([url]);
    }
  };

  public navigateByUrl = (url: string): Promise<boolean> => {
    return this._router.navigateByUrl(url);
  };

  public getCurrentUrl = (): string => {
    return this._router.url;
  };

  public navigateToNewTab(url: string) {
    const navigationUrl = this._router.serializeUrl(this._router.createUrlTree([`/${url}`]));
    window.open(navigationUrl, '_blank');
  }
  // Home Screen
  public navigateToHome = (): Promise<boolean> => {
    const url = 'home';
    return this.navigate(url);
  };
  // Unauthenticated Screen
  public navigateToUnauthenticated = (): Promise<boolean> => {
    const url = 'unauthenticated';
    return this.navigate(url);
  };

  // Events
  public navigateToEventsHome = (params?: Params): Promise<boolean> => {
    return this._configService.hasBetaFeatureEnabled('EventsV4').then((data) => {
      if (data) {
        return this.navigateToEventsHomeV4(params);
      } else {
        return this.navigateToEventsHomeV3(params);
      }
    });
  };

  public navigateToEventsHomeV3 = (params?: Params): Promise<boolean> => {
    const url = 'events';
    if (params) {
      return this.navigate(url, params);
    } else {
      return this.navigate(url);
    }
  };

  public navigateToEventsHomeV4 = (params?: Params): Promise<boolean> => {
    const url = 'events-v4';

    if (params) {
      return this.navigate(url, params);
    } else {
      return this.navigate(url);
    }
  };

  public navigateToEvent = (eventId: number): Promise<boolean> => {
    const url = 'event/' + eventId.toString();
    return this.navigate(url);
  };

  public navigateToEventV4 = (eventId: number): Promise<boolean> => {
    const url = 'events-v4/' + eventId.toString();
    return this.navigate(url);
  };

  public navigateToEventSettings = (eventId: number): Promise<boolean> => {
    const url = 'event/' + eventId.toString() + '/settings';
    return this.navigate(url);
  };

  public navigateToEventV4Settings = (eventId: number): Promise<boolean> => {
    const url = 'events-v4/' + eventId.toString() + '/settings';
    return this.navigate(url);
  };

  public navigateToEventRegistrationNew = (eventId: number): Promise<boolean> => {
    const url = '/event/' + eventId.toString() + '/registration/new';
    return this.navigate(url);
  };

  public navigateToEventV4RegistrationNew = (eventId: number): Promise<boolean> => {
    const url = '/events-v4/' + eventId.toString() + '/registration/new';
    return this.navigate(url);
  };

  public navigateToEventRegistrationEdit = (eventId: number, registrationId: number): Promise<boolean> => {
    const url = '/event/' + eventId.toString() + '/registration/' + registrationId.toString();
    return this.navigate(url);
  };

  public navigateToEventV4RegistrationEdit = (eventId: number, registrationId: number): Promise<boolean> => {
    const url = '/events-v4/' + eventId.toString() + '/registration/' + registrationId.toString();
    return this.navigate(url);
  };

  public navigateToEventItemNew = (eventId: number): Promise<boolean> => {
    const url = '/event/' + eventId.toString() + '/items/new';
    return this.navigate(url);
  };

  public navigateToEventV4ItemNew = (eventId: number): Promise<boolean> => {
    const url = '/events-v4/' + eventId.toString() + '/items/new';
    return this.navigate(url);
  };

  // Legacy web client integration
  public popupSafeNavigate = (url: string): void => {
    let newWindow = window.open(url);
    setTimeout(() => {
      if (!newWindow || newWindow.innerHeight <= 0) {
        this._dialogService.error(this._translateService.instant('common.popup-blocked-error'));
      }
    }, 200);
  };

  public navigateToLegacyWebClientHome = (): Promise<boolean> => {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url = this.legacyDomain + '/external/wcpages/v12/webclient/index.aspx?token=' + data;
      window.open(url);
      return Promise.resolve(true);
    });
  };
  public navigateToLegacyWebClientProfileSearch = (): Promise<boolean> => {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url = this.legacyDomain + '/external/wcpages/v12/webclient/Profile/index.aspx?token=' + data;
      window.open(url);
      return Promise.resolve(true);
    });
  };

  public navigateToLegacyWebClientAdmin = (): Promise<boolean> => {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url = this.legacyDomain + '/external/wcpages/v12/webclient/admin/index.aspx?token=' + data;
      window.open(url);
      return Promise.resolve(true);
    });
  };

  public navigateToLegacyWebClientBigCommerceSetup(): Promise<boolean> {
    return this.navigateToLegacyPage('/v12/webclient/bigcommerce/load.aspx');
  }

  public navigateToLegacyWebClientConfigSettings(): Promise<boolean> {
    return this.navigateToLegacyPage('/v12/webclient/weblink/settings/edit_embedded.aspx');
  }

  public navigateToLegacyWebClientReports = (): Promise<boolean> => {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url = this.legacyDomain + '/external/wcpages/v12/webclient/report/index.aspx?token=' + data;
      window.open(url);
      return Promise.resolve(true);
    });
  };

  public navigateToLegacyWebClientRestoreProfile(): Promise<boolean> {
    return this.navigateToLegacyPage('/v12/webclient/admin/restore/restoreprofile.aspx');
  }

  public navigateToLegacyWebClientMergeProfile(): Promise<boolean> {
    return this.navigateToLegacyPage('/v12/webclient/admin/merge/profilemerge.aspx');
  }

  public navigateToLegacyWebClientImportInvoices(): Promise<boolean> {
    return this.navigateToLegacyPage('/v12/webclient/revenue/invoice/import/index.aspx');
  }

  public navigateToLegacyWebClientBatchDropSettings(): Promise<boolean> {
    return this.navigateToLegacyPage('/v12/webclient/admin/membership/batchdropsettings.aspx');
  }

  public navigateToLegacyWebClientMemberDropActions(): Promise<boolean> {
    return this.navigateToLegacyPage('/v12/webclient/admin/membership/dropactionsettings.aspx');
  }

  public navigateToLegacyWebClientMembershipLevelManagement(): Promise<boolean> {
    return this.navigateToLegacyPage('/v12/webclient/admin/membership/level/management.aspx');
  }

  public navigateToLegacyWebClientProfileSelectorSettings(): Promise<boolean> {
    return this.navigateToLegacyPage('/v12/webclient/profile/selector/settings.aspx');
  }

  public navigateToLegacyCouponCrudPage(profileId: number): Promise<boolean> {
    const encryptedProfileId = this.generateLegacyDJEncryptionValue(profileId);
    return this.navigateToLegacyPage('/External/WCPages/WCCoupons/Couponadmin.aspx', {
      ProfileID: encryptedProfileId,
    });
  }

  public navigateToModernCouponAdmin(profileId: number) {
    let token = this._authService.getAccessToken();
    let url = `${this.legacyDomain}/atlas/coupons/${profileId}/coupon-admin#access_token=${token}`;
    this.popupSafeNavigate(url);
  }

  public navigateToLegacyProfileChangeRequestsPage(): Promise<boolean> {
    return this.navigateToLegacyPage('/external/wcpages/v12/webclient/Profile/update/manage-requests.aspx');
  }

  public navigateToLegacyUndropMembersPage(): Promise<boolean> {
    return this.navigateToLegacyPage('/external/wcpages/v12/webclient/profile/membership/batchdropmanagement.aspx');
  }

  public navigateToBenefitsManagement = (): Promise<boolean> => {
    const url = 'benefits/management';
    return this.navigate(url);
  };

  public navigateToProfileBenefitsManagement = (profileId: number): Promise<boolean> => {
    const url = 'benefits/profile/' + profileId.toString();
    return this.navigate(url);
  };

  public navigateToEngagementReport = (profileId: number): Promise<boolean> => {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain +
        '/external/wcpages/v12/webclient/report/report.aspx?token=' +
        data +
        '&reportname=Engagement_Report.rpt&profileid=' +
        profileId.toString();
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  };

  public navigateToEventParticipationEngagementReport = (): Promise<boolean> => {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain + '/external/wcpages/v12/webclient/report/report.aspx?token=' + data + '&reportname=Engagement_EventSignUps.rpt';
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  };

  public navigateToContactEngagementReport = (): Promise<boolean> => {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain + '/external/wcpages/v12/webclient/report/report.aspx?token=' + data + '&reportname=Engagement_Contacts.rpt';
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  };

  public navigateToNonDuesRevenueEngagementReport = (): Promise<boolean> => {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain +
        '/external/wcpages/v12/webclient/report/report.aspx?token=' +
        data +
        '&reportname=Engagement_Non-Dues_Revenue.rpt';
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  };

  public navigateToReferralRateEngagementReport = (): Promise<boolean> => {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain + '/external/wcpages/v12/webclient/report/report.aspx?token=' + data + '&reportname=Engagement_Referrals.rpt';
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  };

  public navigateToCommitteeParticipationEngagementReport = (): Promise<boolean> => {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain + '/external/wcpages/v12/webclient/report/report.aspx?token=' + data + '&reportname=Engagement_Committees.rpt';
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  };

  public navigateRenwalStatisticsReport() {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain + '/external/wcpages/v12/webclient/report/report.aspx?token=' + data + '&reportname=Renewal_Statistics.rpt';
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  }
  public navigateRevenuePledgeCampaignTotalsReport(CampaignId) {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain +
        '/external/wcpages/v12/webclient/report/report.aspx?token=' +
        data +
        `&reportname=Revenue_PledgeCampaignTotals.rpt&CampaignID=${CampaignId}`;
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  }
  public navigateRevenuePledgesAndInvoicesReport() {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain +
        '/external/wcpages/v12/webclient/report/report.aspx?token=' +
        data +
        '&reportname=Revenue_PledgesAndInvoices.rpt';
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  }

  public navigateToRevenueDebitCreditTransactionsByBatchPostingReport(
    begin: Date | number | string,
    end: Date | number | string,
    batchNumber: string
  ) {
    let begindate = formatDate(begin, 'yyyy-MM-dd', 'en_US');
    let enddate = formatDate(end, 'yyyy-MM-dd', 'en_US');
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain +
        '/external/wcpages/v12/webclient/report/report.aspx?token=' +
        data +
        '&reportname=Revenue_DebitCreditTransactionsByBatchPosting.rpt' +
        '&begindate=' +
        begindate +
        '&enddate=' +
        enddate +
        '&batch=' +
        batchNumber +
        '&show_details=details';
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  }

  // Revenue
  public navigateToInvoiceReport(invoiceNum: number): Promise<boolean> {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain +
        '/external/wcpages/v12/webclient/report/report.aspx?token=' +
        data +
        '&reportname=Revenue_InvoicesInvoiceNumParam.rpt&invoicenum=' +
        invoiceNum.toString();
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  }

  navigateToInvoiceBatchCreationReport(): Promise<boolean> {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain +
        '/external/wcpages/v12/webclient/report/report.aspx?token=' +
        data +
        '&reportname=Revenue_InvoicesBatchCreationParam.rpt';
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  }

  public navigateToInvoiceReportForMultipleNumbers(invoiceNumbers: number[]) {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain +
        '/external/wcpages/v12/webclient/report/report.aspx?token=' +
        data +
        '&reportname=vir_Revenue_InvoicesByInvoiceNums&invoicenumbers=' +
        invoiceNumbers.join(',');
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  }

  public navigateToStatementForProfileId(profileId: number) {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain +
        '/external/wcpages/v12/webclient/report/report.aspx?token=' +
        data +
        '&reportname=Revenue_StatementProfileIDParam.rpt&ProfileId=' +
        profileId;
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  }

  public navigateToPaymentReceiptForPaymentHeader(paymentHeaderId: number) {
    return this.navigateToLegacyPage('/external/wcpages/v12/webclient/report/report.aspx', {
      reportname: 'Revenue_PaymentReceiptPaymentHeaderIDParam.rpt',
      PaymentHeaderId: paymentHeaderId,
    });
  }

  public navigateToOnlineBillPay(profileId: number) {
    const encryptedId = this.generateLegacyDJEncryptionValue(profileId);
    const url = `${this.legacyDomain}/external/wcpages/wcecommerce/onlinebillpaypage.aspx?ProfileID=${encryptedId}`;
    this.popupSafeNavigate(url);
  }

  public navigateCreditCardProcessing = (): Promise<boolean> => {
    const url = '/revenue/epayment/creditcard/charge';
    return this.navigate(url);
  };

  public navigateCardConnectTransactions = (): Promise<boolean> => {
    const url = '/revenue/epayment/cardpointe/transactions';
    return this.navigate(url);
  };

  public navigateToARSummaryReport(): Promise<boolean> {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain +
        '/external/wcpages/v12/webclient/report/report.aspx?token=' +
        data +
        '&reportname=Revenue_AccountsReceivableSummary.rpt';
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  }

  public navigateToRevenueByTypeReport(): Promise<boolean> {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain + '/external/wcpages/v12/webclient/report/report.aspx?token=' + data + '&reportname=Revenue_RevenueByType.rpt';
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  }

  public navigateToPaymentsAppliedReport(): Promise<boolean> {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain +
        '/external/wcpages/v12/webclient/report/report.aspx?token=' +
        data +
        '&reportname=Revenue_PaymentsAndTheRevenueItemsTheyWereAppliedAgainst.rpt';
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  }
  public navigateToRevenueRecognitionReport(): Promise<boolean> {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain +
        '/external/wcpages/v12/webclient/report/report.aspx?token=' +
        data +
        '&reportname=Revenue_Recognition_OneTwelfth_Adj.rpt';
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  }

  public navigateToReport(reportName: string): Promise<boolean> {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url = this.legacyDomain + `/external/wcpages/v12/webclient/report/report.aspx?token=${data}&reportname=${reportName}`;
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  }

  public navigateToInvoiceCreate(profileId: number) {
    return this.navigate(`/revenue/invoice/new`, { profileId });
  }

  public navigateToInvoiceEdit(invoiceNum: number): Promise<boolean> {
    return this.navigate(`/revenue/invoice/${invoiceNum}`);
  }

  // communicator
  public navigateToCommunicationNewEmailMessage = (): Promise<boolean> => {
    const url = '/communications/message/draft/0';
    return this.navigate(url);
  };

  public navigateToFormBuilderDashboard = (): Promise<boolean> => {
    const url = '/forms';
    return this.navigate(url);
  };

  // forms
  public navigateToFormBuilderCreateTemplate = (): Promise<boolean> => {
    const url = '/forms/formbuilder';
    return this.navigate(url);
  };
  public navigateToFormBuilderTemplate = (id: number): Promise<boolean> => {
    const url = '/forms/formbuilder/' + id.toString();
    return this.navigate(url);
  };

  /*
  Navigates to the v2 forms or joinform
  */
  public navigateToFormEdit = (id: number, formType: FormV2TypeEnum): Promise<boolean> => {
    let url = '';
    // seperate pages for form edits atm, but we might merge the v2 versions into one that page to rule them all
    switch (formType) {
      case FormV2TypeEnum.Donation:
        url = `/form-builder/donation/${id}/edit`;
        break;
      case FormV2TypeEnum.GeneralSurvey:
        url = `/form-builder/general/${id}/edit`;
        break;
      case FormV2TypeEnum.Join:
        // join form not part of v2 at the moment
        url = `/forms/formbuilder/${id}`;
        break;
      case FormV2TypeEnum.Renewal:
        url = `/form-builder/renewal/${id}/edit`;
        break;
      case FormV2TypeEnum.Prospect:
        url = `/form-builder/prospect/${id}/edit`;
        break;
    }
    return this.navigate(url);
  };

  // Profiles
  public navigateToProfileFinder = (params?: Params): Promise<boolean> => {
    const url = '/profiles';
    if (params) {
      return this.navigate(url, params);
    } else {
      return this.navigate(url);
    }
  };

  public navigateToProfileFinderNewTab = () => {
    this.navigateToNewTab('profiles');
  };

  public navigateToProfileView = (id: number, scrollToTop: boolean = false): Promise<boolean> => {
    const url = '/profile/' + id.toString();
    if (scrollToTop) {
      window.scrollTo(0, 0);
    }
    return this.navigate(url);
  };

  public navigateToProfileEdit = (id: number): Promise<boolean> => {
    const url = `/profile/${id}/edit`;
    return this.navigate(url);
  };

  public navigateToSalesOpportunities() {
    return this.navigate('profiles/salesopportunities');
  }

  public navigateToContactRecords() {
    return this.navigate('profiles/contactrecords');
  }

  public navigateToTasks() {
    return this.navigate('profiles/tasks');
  }

  public navigateToMembersJoinedReport(): Promise<boolean> {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain + '/external/wcpages/v12/webclient/report/report.aspx?token=' + data + '&reportname=Member_MemberJoined.rpt';
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  }

  public navigateToMemberRetentionReport(): Promise<boolean> {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain + '/external/wcpages/v12/webclient/report/report.aspx?token=' + data + '&reportname=Member_RetentionReport.rpt';
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  }

  public navigateToMembersDroppedReport(): Promise<boolean> {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain + '/external/wcpages/v12/webclient/report/report.aspx?token=' + data + '&reportname=Member_MemberDropped.rpt';
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  }

  private navigateToLegacyPage(aspxPath: string, params: object = {}): Promise<boolean> {
    const stringParams = Object.entries(params)
      .map((e) => e.join('='))
      .join('&');
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url = this.legacyDomain + `${aspxPath}?token=${data}&${stringParams}`;
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  }

  public navigateToReferralReportPage(profileId: number): Promise<boolean> {
    const url = `/referral/refferalValueReport/${profileId}`;
    return this.navigate(url);
  }

  public navigateToLegacyReferralReportPage(profileId: number): Promise<boolean> {
    return this._userService.getCurrentUserLegacyToken().then((data: string) => {
      const url =
        this.legacyDomain +
        '/external/wcpages/v12/referral/referralreport.aspx?ProfileId=' +
        this.generateLegacyDJEncryptionValue(profileId);
      this.popupSafeNavigate(url);
      return Promise.resolve(true);
    });
  }

  public navigateToProfileWebPortal(profileId: number): Promise<boolean> {
    return this._configService.hasBetaFeatureEnabled('MemberPortal').then((hasToggle) => {
      let url: string;
      if (hasToggle) {
        url = `${this.legacyDomain}/atlas/portal/profile/${profileId}`;
      } else {
        url = `${this.legacyDomain}/External/WCPages/WCPortal/PortalStartPage.aspx?ProfileID=${this.generateLegacyDJEncryptionValue(
          profileId
        )}`;
      }
      this.popupSafeNavigate(url);
      return true;
    });
  }

  private generateLegacyDJEncryptionValue(id: number): string {
    const junkArray = Array.from('ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789');
    const charArray = Array.from(id.toString());
    const encArray = [];

    for (let i = 0; i < charArray.length; i++) {
      let propVal = parseInt(charArray[i], 10) + 1;
      if (propVal === 10) {
        propVal = 0;
      }
      encArray.push(propVal);
      const j = Math.floor(Math.random() * Math.floor(junkArray.length - 1));
      encArray.push(junkArray[j]);
    }

    return encArray.join('');
  }

  navigateToTransactionsView(profileId: number) {
    return this.navigate(`/profile/${profileId}/transactions`);
  }

  public navigateToApplyPayment(): Promise<boolean> {
    const url = '/revenue/invoices';
    const params = { applyPayment: true } as Params;
    return this.navigate(url, params);
  }

  /**
   * `replaceUrl` defaults to `false` (today's/Angular's default behavior) so existing callers are
   * unaffected. Callers that want URL-state updates (e.g. tab selection) to not push a new history
   * entry per change -- such as ManageCodesComponent -- must pass `replaceUrl = true` explicitly.
   */
  public setCurrentQueryParams(queryParams: object, currentRoute: ActivatedRoute, replaceUrl = false): Promise<boolean> {
    return this._router.navigate([], {
      relativeTo: currentRoute,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }

  public navigateToCommittees() {
    return this.navigate('profiles/committees');
  }

  public navigateToCommitteeEdit(committeeId: number) {
    const url = `/profiles/committee/${committeeId}`;
    return this.navigate(url);
  }

  public navigateToCommitteeMeetingEdit(committeeId: number, meetingId: number) {
    return this.navigate(`/profiles/committee/${committeeId}/meeting/${meetingId}`);
  }

  public getLegacyOnlineBillPayLink(profileId: number): string {
    const encryptedId = this.generateLegacyDJEncryptionValue(profileId);
    const url = `${this.legacyDomain}/external/wcpages/wcecommerce/onlinebillpaypage.aspx?ProfileID=${encryptedId}`;
    return url;
  }

  public getShareInvoiceLink(profileGuid: string, invoiceNum: number): string {
    return `${this.legacyDomain}/external/wcpages/wcecommerce/viewinvoice.aspx?invoicenum=${invoiceNum}&profileGuid=${profileGuid}`;
  }

  public getNewOnlineBillPayLink() {
    return `${this.legacyDomain}/atlas/portal/open-invoices`;
  }

  public navigateToNewForm(formType: FormV2TypeEnum) {
    return this.navigate('/form-builder/new', { type: formType });
  }

  public navigateToMergeProfiles(toKeep?: number, toDelete?: number) {
    return this.navigate('/profiles/merge-profiles', { toKeep, toDelete });
  }

  public navigateToEcomSpa() {
    let url = `${this.legacyDomain}/atlas/ecommerce`;
    this.popupSafeNavigate(url);
  }

  public navigateToArticlesSpa() {
    let url = `${this.legacyDomain}/atlas/articles`;
    this.popupSafeNavigate(url);
  }
  public navigateToEventCalendarSpa() {
    let url = `${this.legacyDomain}/atlas/calendar`;
    this.popupSafeNavigate(url);
  }
  public navigateToDirectorySpa() {
    let url = `${this.legacyDomain}/atlas/directory/search`;
    this.popupSafeNavigate(url);
  }

  public navigateToInvoiceFinder() {
    return this.navigate('/revenue/invoices');
  }

  public navigateToPledgeCampaignEdit(pledgeCampaignId: number) {
    const url = `/revenue/pledge-campaigns/${pledgeCampaignId}`;
    return this.navigate(url);
  }

  public navigateToPledgeEdit(pledgeId: number) {
    const url = `/revenue/pledge/${pledgeId}`;
    return this.navigate(url);
  }

  public navigateToEmptyQueryTool() {
    const url = `/reports/querytool/`;
    return this.navigate(url);
  }

  public navigateToQueryTool(id: number, command: string = null) {
    if (command) {
      const url = `/reports/querytool/${id}/${command}`;
      return this.navigate(url);
    } else {
      const url = `/reports/querytool/${id}`;
      return this.navigate(url);
    }
  }

  public navigateToInsights() {
    this._appManagerService.getInsightsUrl().then((url: string) => {
      this.popupSafeNavigate(url);
    });
  }
}
